import { useCallback, useMemo, useState } from "react";

import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { Button, Checkbox, Grid, Message } from "semantic-ui-react";

import actions from "actions";
import api from "api";
import { useAppSelector } from "store";

import SettingActions from "components/lib/SettingActions";

const OrgTagChooser = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useAppSelector((state) => state.user);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const tagList = user?.ownerOrganisation?.tagList;
  const availableUserTags = useMemo(
    () =>
      tagList?.filter((tag) => !tag?.disabledEntities?.includes("users")) ?? [],
    [tagList],
  );

  const toggleTag = useCallback((tag) => {
    setTagIds((prevTags) => {
      const newTags = [...prevTags];
      const tagIndex = newTags.indexOf(tag._id);
      if (tagIndex > -1) {
        newTags.splice(tagIndex, 1);
      } else {
        newTags.push(tag._id);
      }
      return newTags;
    });
  }, []);

  const saveTags = useCallback(() => {
    setSaving(true);
    api.tags.tagDocuments(
      "users",
      tagIds.filter((tagId) => !(user.tags || []).includes(tagId)), // Only send NEW tags
      [user._id],
      (ownerTags) => {
        setSaving(false);
        dispatch(actions.user.updateTags(ownerTags));
      },
      () => {
        setSaving(false);
        toast.error("Failed to select tags");
      },
    );
  }, [tagIds, user._id, user.tags, dispatch]);

  return (
    <>
      <Helmet title={t("tags.title")} />
      <div
        style={{
          height: "100%",
          maxHeight: "69vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <h3>{t("tags.selectTags")}</h3>
        <p>{t("tags.info", { orgName: user?.ownerOrganisation?.name })}</p>
        <div>
          <Message info>{t("tags.chooseMany")}</Message>
        </div>

        <div
          style={{
            overflowY: "auto",
            overflowX: "hidden",
            flex: 1,
            marginTop: 20,
          }}
        >
          <Grid stackable columns={2}>
            {availableUserTags.map((c) => (
              <Grid.Column key={c._id}>
                <Checkbox
                  toggle
                  style={{ padding: "3px" }}
                  checked={tagIds.indexOf(c._id) > -1}
                  onChange={() => toggleTag(c)}
                  label={c.value}
                />
              </Grid.Column>
            ))}
          </Grid>
        </div>
        <SettingActions>
          <Checkbox
            toggle
            style={{ marginRight: 30, marginBottom: 10 }}
            label={tagIds.length > 0 ? "Deselect all" : "Select All"}
            checked={tagIds.length > 0}
            onChange={() => {
              if (tagIds.length > 0) {
                setTagIds([]);
              } else {
                setTagIds(availableUserTags.map((t) => t._id));
              }
            }}
          />
          <Button
            onClick={() => {
              saveTags();
            }}
            loading={saving}
            primary
            disabled={!tagIds?.length}
            content={t("generic.continue")}
          />
        </SettingActions>
      </div>
    </>
  );
};

export default OrgTagChooser;
